import type { Questionnaire } from "@medplum/fhirtypes";
import { PERFORMANCE_MEASURE_DEFINITION_KIND } from "@repo/api";
import { type Collection, type InitialQueryBuilder, max, toArray } from "@tanstack/db";
import { and, eq, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import type { QuestionnaireRow } from "../fhir/types";
import { revisionSnapshot } from "./definition-revision-snapshot";
import type { DefinitionRevisionChange } from "./definition-version-diff";
import { diffDefinitionRevisions } from "./definition-version-diff";

export interface QuestionnaireDefinitionHistoryDto {
  definitionUrl: string;
  title: string;
  versions: {
    resourceId: string;
    version: string;
    authoredAt?: string;
    /** Display name of whoever wrote the revision; absent when unattributed. */
    author?: string;
    status: "active" | "retired" | "draft" | "unknown";
    current: boolean;
    changes: DefinitionRevisionChange[];
  }[];
}

export interface QuestionnaireDefinitionHistoryFilters {
  definitionUrl?: string;
  performance?: boolean;
}

interface HistoryQueryRow {
  definitionUrl: string;
  title: string;
  revisions: Questionnaire[];
}

export function useQuestionnaireDefinitionHistory(
  definitionUrl: string | undefined,
  performance = false,
): QuestionnaireDefinitionHistoryDto | undefined {
  const definitions = useCollection("questionnaire");
  const result = useLiveQuery({
    query: (query) => {
      if (!definitionUrl) return undefined;
      return buildQuestionnaireDefinitionHistoryRowsQuery(query, definitions, { definitionUrl, performance }).findOne();
    },
  });
  return result.data ? projectHistoryRow(result.data as HistoryQueryRow, performance) : undefined;
}

export function useQuestionnaireDefinitionHistories(
  filters: QuestionnaireDefinitionHistoryFilters = {},
): QuestionnaireDefinitionHistoryDto[] {
  return useQuestionnaireDefinitionHistoryQuery(filters);
}

function useQuestionnaireDefinitionHistoryQuery(
  filters: QuestionnaireDefinitionHistoryFilters,
): QuestionnaireDefinitionHistoryDto[] {
  const definitions = useCollection("questionnaire");
  const result = useLiveQuery({
    query: (query) => buildQuestionnaireDefinitionHistoryRowsQuery(query, definitions, filters),
  });

  // Revision diffs compare adjacent decoded snapshots, which the query builder
  // cannot express. The query still owns lineage filtering, grouping, ordering
  // and nested composition; this pure final step performs only that diff.
  return ((result.data as HistoryQueryRow[] | undefined) ?? []).map((row) =>
    projectHistoryRow(row, filters.performance),
  );
}

export function buildQuestionnaireDefinitionHistoryRowsQuery(
  query: InitialQueryBuilder,
  definitions: Collection<QuestionnaireRow, string>,
  filters: QuestionnaireDefinitionHistoryFilters = {},
) {
  const candidates = query
    .from({ candidate: definitions })
    .where(({ candidate }) =>
      filters.performance
        ? eq(candidate.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND)
        : eq(candidate.questionnaireDefinition, true),
    );
  const selected = filters.definitionUrl
    ? candidates.where(({ candidate }) => eq(candidate.url, filters.definitionUrl))
    : candidates;
  const latestVersions = selected
    .groupBy(({ candidate }) => candidate.url)
    .select(({ candidate }) => ({ url: candidate.url, version: max(candidate.catalogVersion) }));
  return query
    .from({ definition: definitions })
    .innerJoin({ latestVersion: latestVersions }, ({ definition, latestVersion }) =>
      eq(definition.url, latestVersion.url),
    )
    .where(({ definition, latestVersion }) => eq(definition.catalogVersion, latestVersion.version))
    .where(({ definition }) =>
      filters.performance
        ? eq(definition.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND)
        : eq(definition.questionnaireDefinition, true),
    )
    .orderBy(({ definition }) => definition.title, "asc")
    .select(({ definition }) => ({
      definitionUrl: definition.url,
      title: definition.title,
      revisions: toArray(
        query
          .from({ revision: definitions })
          .where(({ revision }) =>
            and(
              filters.performance
                ? eq(revision.catalogModelKind, PERFORMANCE_MEASURE_DEFINITION_KIND)
                : eq(revision.questionnaireDefinition, true),
              eq(revision.url, definition.url),
            ),
          )
          .orderBy(({ revision }) => revision.catalogVersion, "desc")
          .orderBy(({ revision }) => revision.id, "asc")
          .select(({ revision }) => ({ ...revision })),
      ),
    }));
}

function projectHistoryRow(row: HistoryQueryRow, performance = false): QuestionnaireDefinitionHistoryDto {
  return {
    definitionUrl: row.definitionUrl,
    title: row.title,
    versions: row.revisions.map((revision, index) => {
      const current = revisionSnapshot(performance ? "performance-measure" : "questionnaire", revision);
      const prior = row.revisions[index + 1]
        ? revisionSnapshot(performance ? "performance-measure" : "questionnaire", row.revisions[index + 1])
        : undefined;
      return {
        resourceId: revision.id ?? "",
        version: revision.version ?? "1",
        ...(revision.date || revision.meta?.lastUpdated
          ? { authoredAt: revision.date || revision.meta?.lastUpdated }
          : {}),
        ...(revision.meta?.author?.display ? { author: revision.meta.author.display } : {}),
        status:
          revision.status === "active" || revision.status === "retired" || revision.status === "draft"
            ? revision.status
            : "unknown",
        current: index === 0,
        changes: prior ? diffDefinitionRevisions(prior, current) : [{ label: "Definition created", type: "created" }],
      };
    }),
  };
}
